
import React from 'react';
import { Helmet } from 'react-helmet';

interface DynamicSeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogType?: string;
  ogImage?: string;
  url?: string;
  canonicalUrl?: string;
  structuredData?: Record<string, any> | Array<Record<string, any>>;
  noIndex?: boolean;
  // Dynamic keyword generation based on content
  entityName?: string; // Product title or Store name
  category?: string;
  location?: string;
  price?: number;
  condition?: string;
  // Additional meta tags for enhanced SEO
  twitterCard?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

export const DynamicSeoHead: React.FC<DynamicSeoHeadProps> = ({
  title,
  description,
  keywords,
  ogType = 'website',
  ogImage = 'https://treguim.org/logo.svg',
  url,
  canonicalUrl,
  structuredData,
  noIndex = false,
  entityName,
  category,
  location,
  price,
  condition,
  twitterCard = 'summary_large_image',
  author = 'TreguIm',
  publishedTime,
  modifiedTime,
  section,
  tags = [],
}) => {
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const canonical = canonicalUrl || currentUrl;

  // Enhanced Albanian marketplace keywords with new additions
  const baseKeywords = [
    "treguim", "tregu im", "blej online", "shit online", "dyqan online", "platforme shitblerje", 
    "oferta", "shpallje falas", "njoftime falas", "njoftime", "produkte te perdorura", "produkte te reja", 
    "cmime te mira", "shitje me pakice", "e-commerce shqiperi", "tregti elektronike",
    "gjej produkte", "kerko shpallje", "blerje te sigurta", "shitje te shpejta",
    // New Albanian keywords
    "shesblej", "merrjep", "celsi", "shesblej online", "merrjep produkte", "celsi shitje",
    "shesblej celsi", "merrjep njoftime", "njoftime celsi", "shesblej falas", "merrjep online",
    "celsi produktet", "shesblej dyqan", "merrjep oferta", "njoftime shitje", "celsi blerje"
  ];

  // Generate dynamic keywords based on content
  const generateDynamicKeywords = (): string[] => {
    const dynamicKeywords: string[] = [];

    if (entityName) {
      // Add entity-specific keywords
      const entityWords = entityName.toLowerCase().split(' ');
      dynamicKeywords.push(...entityWords);
      
      // Add "blej" + entity name variations
      dynamicKeywords.push(`blej ${entityName.toLowerCase()}`);
      dynamicKeywords.push(`${entityName.toLowerCase()} ne shitje`);
      dynamicKeywords.push(`${entityName.toLowerCase()} online`);
    }

    if (category) {
      dynamicKeywords.push(category.toLowerCase());
      dynamicKeywords.push(`${category.toLowerCase()} ne shitje`);
      dynamicKeywords.push(`blej ${category.toLowerCase()}`);
    }

    if (location) {
      dynamicKeywords.push(location.toLowerCase());
      if (entityName) {
        dynamicKeywords.push(`${entityName.toLowerCase()} ${location.toLowerCase()}`);
      }
      if (category) {
        dynamicKeywords.push(`${category.toLowerCase()} ${location.toLowerCase()}`);
      }
    }

    if (price) {
      dynamicKeywords.push("cmime te mira");
      dynamicKeywords.push("okazion");
      if (price < 100) {
        dynamicKeywords.push("lire");
      }
    }

    if (condition) {
      if (condition === 'new') {
        dynamicKeywords.push("i ri", "e re", "produkt i ri");
      } else {
        dynamicKeywords.push("i perdorur", "e perdorur", "si i ri");
      }
    }

    // Add tags if provided
    if (tags.length > 0) {
      dynamicKeywords.push(...tags);
    }

    return dynamicKeywords;
  };

  const dynamicKeywords = generateDynamicKeywords();
  const allKeywords = [...dynamicKeywords, ...baseKeywords];
  const combinedKeywords = keywords ? `${keywords}, ${allKeywords.join(', ')}` : allKeywords.join(', ');

  // Ensure structured data is properly formatted
  const renderStructuredData = () => {
    if (!structuredData) return null;
    
    if (Array.isArray(structuredData)) {
      // Multiple schemas
      return structuredData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ));
    } else {
      // Single schema
      return (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      );
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={combinedKeywords} />
      <meta name="author" content={author} />
      
      {/* Enhanced Meta Tags for SEO */}
      <meta name="application-name" content="TreguIm" />
      <meta name="msapplication-TileColor" content="#16a34a" />
      <meta name="theme-color" content="#16a34a" />
      
      {/* Language and Region */}
      <meta name="language" content="Albanian" />
      <meta name="geo.region" content="AL" />
      <meta name="geo.placename" content="Albania" />
      
      {/* Article specific meta tags */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}
      
      {/* Robots Meta Tag */}
      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="TreguIm" />
      <meta property="og:locale" content="sq_AL" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@treguim" />
      <meta name="twitter:creator" content="@treguim" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* App Links for mobile apps */}
      <meta property="al:web:url" content={currentUrl} />
      <meta property="al:android:url" content={currentUrl} />
      <meta property="al:ios:url" content={currentUrl} />
      
      {/* Structured Data / JSON-LD */}
      {renderStructuredData()}
    </Helmet>
  );
};
